// Tip rules shared by the app (app/tips.ts) and the server's evening push job (server/tips-job.ts).
// Times inside a forecast are the forecast place's wall-clock time stored as if UTC, so "tonight" means the
// same thing on the phone and on the server (which runs on UTC).
import type { Kind } from "./model.ts";
import { compassIndex, type ListKey, type ListOf, type TextKey, type Translate } from "./strings.ts";

export const PREDATORS = ["pike", "zander", "perch", "browntrout", "rainbowtrout", "seatrout", "salmon", "asp", "burbot", "grayling", "char"];
const HOURLY = "wind_speed_10m,wind_gusts_10m,wind_direction_10m,temperature_2m,precipitation,snowfall,pressure_msl,cloud_cover";

export interface ForecastHour {
  /** Local wall-clock time as if UTC, ms. */
  t: number;
  wind: number;
  gust: number;
  dir: number;
  temp: number;
  rain: number;
  snow: number;
  pressure: number;
  cloud: number;
}
export interface Forecast {
  at: { lat: number; lon: number };
  fetched: number;
  /** The place's offset from UTC, seconds. */
  offset: number;
  hours: ForecastHour[];
}

export type Trend = "falling" | "rising" | "steady";

/** Conditions learned from your own catches of one species (app/insights.ts). */
export interface LearnedPattern {
  species: string;
  kind: Kind;
  name: string;
  n: number;
  fromH: number;
  toH: number;
  windMax: number;
  trend: Trend | null;
  summary: string;
}

/** How you fish and hunt: method, style and species codes. */
export interface TipProfile {
  fishMethods: string[];
  fishSpecies: string[];
  huntStyles: string[];
  huntSpecies: string[];
  learned?: LearnedPattern[];
}

export interface Tip {
  id: string;
  kind: "good" | "warn";
  icon: Kind;
  title: string;
  text: string;
  score: number;
}

/** The words, numbers and time format of the phone (or of the language a push goes out in). */
export interface TipContext {
  t: Translate;
  list: ListOf;
  num: (n: number, digits?: number) => string;
  when: (localMs: number) => string;
  /** Real time now, ms (for tests). */
  now?: number;
}

export function forecastUrl(lat: number, lon: number): string {
  return `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}&longitude=${lon.toFixed(3)}` +
    `&timezone=auto&forecast_days=3&wind_speed_unit=ms&hourly=${HOURLY}`;
}

interface OpenMeteoHourly {
  utc_offset_seconds?: number;
  hourly?: { time: string[]; wind_speed_10m: number[]; wind_gusts_10m: number[]; wind_direction_10m: number[]; temperature_2m: number[];
    precipitation: number[]; snowfall: number[]; pressure_msl: number[]; cloud_cover: number[] };
}
export function parseForecast(json: unknown, lat: number, lon: number): Forecast | null {
  const j = json as OpenMeteoHourly | null, h = j?.hourly;
  if (!j || !h || !h.time) return null;
  return {
    at: { lat, lon }, fetched: Date.now(), offset: j.utc_offset_seconds || 0,
    hours: h.time.map((s, i) => ({
      t: Date.parse(s + ":00Z"), wind: h.wind_speed_10m[i], gust: h.wind_gusts_10m[i], dir: h.wind_direction_10m[i], temp: h.temperature_2m[i],
      rain: h.precipitation[i], snow: h.snowfall[i], pressure: h.pressure_msl[i], cloud: h.cloud_cover[i],
    })),
  };
}

type Part = "night" | "evening" | "morning" | "day";
type NumKey = Exclude<keyof ForecastHour, "t">;

/** Up to three tips for the next two days, best first. */
export function makeTips(fc: Forecast | null, profile: TipProfile | null, ctx: TipContext): Tip[] {
  if (!fc || !profile) return [];
  // (older saved profiles may lack some lists)
  const p: Required<TipProfile> = { fishMethods: profile.fishMethods ?? [], fishSpecies: profile.fishSpecies ?? [], huntStyles: profile.huntStyles ?? [],
    huntSpecies: profile.huntSpecies ?? [], learned: profile.learned ?? [] };
  const { t, num } = ctx;
  const nowLocal = (ctx.now ?? Date.now()) + fc.offset * 1000;
  const day0 = Math.floor(nowLocal / 864e5) * 864e5;
  // hours of day `off` (0 = today) between fromH and toH (toH may pass midnight, e.g. 18–32)
  const win = (off: number, fromH: number, toH: number) => fc.hours.filter((x) =>
    x.t >= day0 + off * 864e5 + fromH * 36e5 && x.t < day0 + off * 864e5 + toH * 36e5 && x.t >= nowLocal - 36e5);
  const maxOf = (hs: ForecastHour[], k: NumKey) => Math.max(...hs.map((x) => x[k] ?? 0));
  const sumOf = (hs: ForecastHour[], k: NumKey) => hs.reduce((s, x) => s + (x[k] ?? 0), 0);
  const word = (off: number, part: Part) => t(`when_${part}_${off}` as TextKey);
  // full direction names, e.g. "east wind", "from the east", "west" (lists in the language files)
  const dirWord = (deg: number, list: ListKey) => ctx.list(list)[compassIndex(deg)];
  const has = (list: string[], items: string[]) => items.some((i) => list.includes(i));
  const meanDir = (hs: ForecastHour[]) => {
    let x = 0, y = 0;
    for (const h of hs) { x += Math.cos(h.dir * Math.PI / 180); y += Math.sin(h.dir * Math.PI / 180); }
    return { dir: (Math.atan2(y, x) * 180 / Math.PI + 360) % 360, steady: Math.sqrt(x * x + y * y) / hs.length };
  };
  const hunting = p.huntStyles.length > 0 || p.huntSpecies.length > 0;
  const out: Tip[] = [];

  // Nets, long-lines and trap nets: a calm night is good for setting them; strong wind is a warning.
  if (has(p.fishMethods, ["net", "longline", "trapnet"])) {
    for (const off of [0, 1]) {
      const night = win(off, 18, 32);
      if (night.length < 8) continue;
      const w = maxOf(night, "wind"), g = maxOf(night, "gust");
      if (w >= 9 || g >= 14) out.push({ id: "netwind" + off, kind: "warn", icon: "fish", title: t("tipNetWindTitle", word(off, "night")), text: t("tipNetWindText", num(w, 0), num(g, 0)), score: 3 });
      else if (w <= 4 && g <= 8) { out.push({ id: "netcalm" + off, kind: "good", icon: "fish", title: t("tipNetCalmTitle", word(off, "night")), text: t("tipNetCalmText", num(w, 0), num(g, 0)), score: 2 - off * 0.5 }); break; }
    }
  }
  // Fly fishing: a calm, dry evening.
  if (p.fishMethods.includes("fly")) {
    for (const off of [0, 1]) {
      const eve = win(off, 18, 23);
      if (eve.length < 3) continue;
      const w = maxOf(eve, "wind"), r = sumOf(eve, "rain");
      if (w <= 3 && r < 0.3) { out.push({ id: "fly" + off, kind: "good", icon: "fish", title: t("tipFlyTitle", word(off, "evening")), text: t("tipFlyText", num(w, 0)), score: 1.5 - off * 0.5 }); break; }
    }
  }
  // Predatory fish with lures, jigs, trolling or bait: falling air pressure.
  if (has(p.fishMethods, ["lure", "jig", "trolling", "bait"]) || has(p.fishSpecies, PREDATORS)) {
    const hs = fc.hours.filter((x) => x.t >= nowLocal && x.t < nowLocal + 36 * 36e5);
    let best: { d: number; t: number } | null = null;
    for (let i = 6; i < hs.length; i++) { const d = hs[i].pressure - hs[i - 6].pressure; if (d <= -3 && (!best || d < best.d)) best = { d, t: hs[i - 6].t }; }
    if (best) out.push({ id: "pressure", kind: "good", icon: "fish", title: t("tipPressureTitle"), text: t("tipPressureText", num(Math.abs(best.d), 0), ctx.when(best.t)), score: 1.2 });
  }
  // Stand and bow hunting: a steady morning wind, so you know where your scent goes.
  if (has(p.huntStyles, ["stand", "bow"])) {
    for (const off of [0, 1]) {
      const morning = win(off, 5, 10);
      if (morning.length < 4) continue;
      const m = meanDir(morning), w = maxOf(morning, "wind");
      if (m.steady > 0.9 && w >= 1.5 && w <= 7) {
        out.push({ id: "stand" + off, kind: "good", icon: "game", title: t("tipStandTitle", word(off, "morning"), dirWord(m.dir, "windNames")),
          text: t("tipStandText", dirWord(m.dir, "dirFrom"), dirWord(m.dir + 180, "dirTo"), num(w, 0)), score: 1.4 - off * 0.4 });
        break;
      }
    }
  }
  // Hunting with a dog: dry day with light wind.
  if (p.huntStyles.includes("dog")) {
    for (const off of [0, 1]) {
      const day = win(off, 8, 16);
      if (day.length < 6) continue;
      const w = maxOf(day, "wind"), r = sumOf(day, "rain"), cold = Math.min(...day.map((x) => x.temp));
      if (w <= 6 && r < 1 && cold > -15) { out.push({ id: "dog" + off, kind: "good", icon: "game", title: t("tipDogTitle", word(off, "day")), text: t("tipDogText", num(w, 0)), score: 1.3 - off * 0.4 }); break; }
    }
  }
  // Fresh snow overnight: good for tracking the next morning.
  if (hunting) {
    for (const off of [0, 1]) {
      const night = win(off, 18, 30), morning = win(off + 1, 6, 10);
      if (night.length < 8 || !morning.length) continue;
      const fresh = sumOf(night, "snow"), later = sumOf(morning, "snow");
      if (fresh >= 1 && later < 0.3) { out.push({ id: "snow" + off, kind: "good", icon: "game", title: t("tipSnowTitle", word(off + 1, "morning")), text: t("tipSnowText", num(fresh, 0)), score: 1.6 }); break; }
    }
  }
  // Learned from your own catches: the coming weather looks like the weather you caught the most in.
  for (const L of p.learned) {
    const ok = (x: ForecastHour, i: number, hs: ForecastHour[]) => {
      const hr = new Date(x.t).getUTCHours();
      const inTime = L.fromH <= L.toH ? hr >= L.fromH && hr < L.toH : hr >= L.fromH || hr < L.toH;
      if (!inTime || x.wind > L.windMax) return false;
      if (!L.trend) return true;
      const prev = hs[i - 3]; if (!prev) return false;
      const d = x.pressure - prev.pressure;
      return L.trend === "falling" ? d <= -1 : L.trend === "rising" ? d >= 1 : Math.abs(d) < 1;
    };
    const hs = fc.hours.filter((x) => x.t >= nowLocal - 3 * 36e5 && x.t < nowLocal + 36 * 36e5);
    const match = hs.findIndex((x, i) => x.t >= nowLocal && ok(x, i, hs));
    if (match >= 0) {
      out.push({ id: "learned-" + L.species, kind: "good", icon: L.kind === "game" ? "game" : "fish", title: t("tipLearnedTitle", L.name), text: t("tipLearnedText", L.n, ctx.when(hs[match].t), L.summary), score: 1.8 });
    }
  }
  return out.sort((a, b) => b.score - a.score).slice(0, 3);
}
